/**
 * AudioManager: owns the AudioContext, the category gain graph, the sample
 * cache (real files from the manifest, else procedural placeholders), voice
 * limiting, 3-D panning and the autoplay unlock.
 *
 *   source -> voice gain -> (PannerNode) -> category gain -> master -> destination
 */
import * as THREE from 'three';
import { CATEGORIES, type Category } from './types';
import { SOUND_DEFS, defaultsFor } from './registry';
import { EMPTY_MANIFEST, mergeManifest, parseManifest, pickVariant, resolveSound, type Manifest } from './manifest';
import { makeLoopable, renderSynth } from './synth';
import { VoiceLimiter, type Voice } from './voices';
import { getVolume, onVolumeChange } from './volumes';

export const AUDIO_BASE = `${import.meta.env.BASE_URL}audio/`;
/** Extracted vanilla samples (scripts/extract-game-sounds.mjs); gitignored, so usually absent on a fresh clone. */
const VANILLA_MANIFEST = `${import.meta.env.BASE_URL}mc/sounds/manifest.json`;

export interface PlayOpts {
  /** World position; omitted = non-positional (own-player, UI). */
  pos?: { x: number; y: number; z: number };
  volume?: number;
  pitch?: number;
}

export class AudioManager {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  private readonly gains = new Map<Category, GainNode>();
  manifest: Manifest = EMPTY_MANIFEST;
  private readonly buffers = new Map<string, Promise<AudioBuffer[]>>();
  private readonly fileBuffers = new Map<string, Promise<AudioBuffer>>();
  private readonly lastVariant = new Map<string, number>();
  private readonly voices = new VoiceLimiter();
  private pending: { at: number; fn: () => void }[] = [];
  private readonly fwd = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly pos = new THREE.Vector3();
  readonly ready: Promise<void>;

  constructor() {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    for (const c of CATEGORIES) {
      if (c === 'master') { this.gains.set(c, this.master); continue; }
      const g = this.ctx.createGain();
      g.connect(this.master);
      this.gains.set(c, g);
    }
    for (const c of CATEGORIES) this.gains.get(c)!.gain.value = getVolume(c);
    onVolumeChange((c, v) => this.gains.get(c)?.gain.setTargetAtTime(v, this.ctx.currentTime, 0.03));
    this.installUnlock();
    this.ready = this.loadManifest();
  }

  gain(c: Category): GainNode { return this.gains.get(c)!; }
  get running(): boolean { return this.ctx.state === 'running'; }

  private async loadManifest(): Promise<void> {
    try {
      const r = await fetch(`${AUDIO_BASE}manifest.json`, { cache: 'no-cache' });
      if (r.ok) this.manifest = parseManifest(await r.json());
    } catch { /* no manifest: everything is procedural */ }
    try {
      const r = await fetch(VANILLA_MANIFEST, { cache: 'no-cache' });
      if (r.ok) this.manifest = mergeManifest(this.manifest, parseManifest(await r.json()));
    } catch { /* not extracted */ }
  }

  /** True when the manifest has real samples for this id. */
  has(id: string): boolean { return !!this.manifest.sounds[id]?.length; }

  private file(f: string): Promise<AudioBuffer> {
    let p = this.fileBuffers.get(f);
    if (!p) {
      p = fetch(AUDIO_BASE + f).then(async (r) => {
        if (!r.ok) throw new Error(`${f}: ${r.status}`);
        return this.ctx.decodeAudioData(await r.arrayBuffer());
      });
      this.fileBuffers.set(f, p);
    }
    return p;
  }

  /** Autoplay policy: resume on the first gesture, then flush recent requests. */
  private installUnlock(): void {
    const unlock = (): void => {
      if (this.ctx.state === 'running') return;
      void this.ctx.resume().then(() => {
        const now = performance.now();
        const q = this.pending.filter((p) => now - p.at < 1000);
        this.pending = [];
        for (const p of q) p.fn();
      });
    };
    for (const ev of ['pointerdown', 'keydown', 'touchend', 'mousedown']) window.addEventListener(ev, unlock, { capture: true });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) unlock(); });
  }

  /** Buffers for an id: decoded files when the manifest has them, else synthesized variants. */
  private load(id: string): Promise<AudioBuffer[]> {
    let p = this.buffers.get(id);
    if (p) return p;
    const res = resolveSound(this.manifest, id);
    const synth = (): Promise<AudioBuffer[]> => {
      const def = SOUND_DEFS.get(id);
      if (def?.shareWith && def.shareWith !== id) return this.load(def.shareWith);
      if (!def) return Promise.resolve([]);
      return Promise.all(def.synth.map((layers, i) => renderSynth(id, i, layers)));
    };
    if (res.kind === 'files') {
      p = Promise.all(res.files.map((f) => this.file(f))).catch((e) => { console.warn('audio: falling back to synth for', id, e); return synth(); });
    } else {
      p = synth();
    }
    this.buffers.set(id, p);
    return p;
  }

  /** Warm the cache (render placeholders ahead of first use). */
  preload(ids: Iterable<string>): void {
    void this.ready.then(() => { for (const id of ids) void this.load(id); });
  }

  play(id: string, o: PlayOpts = {}): void {
    const def = SOUND_DEFS.get(id) ?? (this.manifest.sounds[id] ? defaultsFor(id) : undefined);
    if (!def) return;
    if (this.ctx.state !== 'running') {
      this.pending.push({ at: performance.now(), fn: () => this.play(id, o) });
      if (this.pending.length > 32) this.pending.shift();
      return;
    }
    const cat = def?.category ?? 'blocks';
    if (getVolume(cat) <= 0 || getVolume('master') <= 0) return;
    if (o.pos && def?.positional !== false) {
      // cheap cull before decoding / rendering anything
      const l = this.pos;
      const d = Math.hypot(o.pos.x - l.x, o.pos.y - l.y, o.pos.z - l.z);
      if (d > (def?.maxDist ?? 16) * Math.max(1, o.volume ?? 1)) return;
    }
    const requested = performance.now();
    void this.ready.then(() => this.load(id)).then((bufs) => {
      if (!bufs.length || performance.now() - requested > 300) return;
      const last = this.lastVariant.get(id) ?? -1;
      const vi = pickVariant(bufs.length, last, Math.random);
      this.lastVariant.set(id, vi);
      // vanilla per-file volume / pitch (only when the real files, not the fallback, loaded)
      const res = resolveSound(this.manifest, id);
      const tw = res.kind === 'files' && res.files.length === bufs.length ? this.manifest.tweaks[res.files[vi]] : undefined;
      if (tw) o = { ...o, volume: (o.volume ?? 1) * tw[0], pitch: (o.pitch ?? 1) * tw[1] };
      this.start(id, cat, bufs[vi], def?.volume ?? 1, def?.pitch ?? [0.8, 1.2], def?.volVar ?? 0.1,
        def?.positional !== false ? o.pos : undefined, def?.maxDist ?? 16, o);
    });
  }

  private start(id: string, cat: Category, buf: AudioBuffer, vol: number, pitch: [number, number], volVar: number,
    pos: PlayOpts['pos'], maxDist: number, o: PlayOpts): void {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = (pitch[0] + (pitch[1] - pitch[0]) * Math.random()) * (o.pitch ?? 1);
    const g = ctx.createGain();
    const v = vol * (o.volume ?? 1);
    g.gain.value = Math.min(1, v) * (1 - Math.random() * volVar);
    src.connect(g);
    let tail: AudioNode = g;
    if (pos) {
      const p = ctx.createPanner();
      p.panningModel = 'HRTF';
      p.distanceModel = 'linear';
      p.refDistance = 1;
      // vanilla: louder-than-1 sounds carry further (16 * volume blocks)
      p.maxDistance = maxDist * Math.max(1, v);
      p.rolloffFactor = 1;
      if (p.positionX) { p.positionX.value = pos.x; p.positionY.value = pos.y; p.positionZ.value = pos.z; } else p.setPosition(pos.x, pos.y, pos.z);
      g.connect(p);
      tail = p;
    }
    tail.connect(this.gain(cat));
    const voice: Voice = {
      id, start: ctx.currentTime,
      stop: () => { try { src.stop(); } catch { /* already stopped */ } },
    };
    this.voices.add(voice);
    src.onended = () => { this.voices.remove(voice); tail.disconnect(); g.disconnect(); };
    src.start();
  }

  /**
   * Looping bed (rain, nether ambience): non-positional, fades in/out.
   * Returns a handle; setLevel(0) fades it out and stop() releases it.
   */
  loop(id: string): { setLevel(v: number): void; stop(): void } {
    const def = SOUND_DEFS.get(id);
    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.connect(this.gain(def?.category ?? 'ambient'));
    let src: AudioBufferSourceNode | null = null;
    let stopped = false;
    void this.ready.then(() => this.load(id)).then((bufs) => {
      if (stopped || !bufs.length) return;
      src = this.ctx.createBufferSource();
      src.buffer = makeLoopable(this.ctx, bufs[0]);
      src.loop = true;
      src.connect(g);
      src.start();
    });
    const base = def?.volume ?? 1;
    return {
      setLevel: (v) => g.gain.setTargetAtTime(base * v, this.ctx.currentTime, 0.8),
      stop: () => {
        stopped = true;
        g.gain.setTargetAtTime(0, this.ctx.currentTime, 0.5);
        setTimeout(() => { try { src?.stop(); } catch { /* not started */ } g.disconnect(); }, 3000);
      },
    };
  }

  /** Listener follows the camera (position, forward, up). */
  updateListener(camera: THREE.Camera): void {
    const l = this.ctx.listener;
    camera.getWorldPosition(this.pos);
    camera.getWorldDirection(this.fwd);
    this.up.set(0, 1, 0).applyQuaternion(camera.getWorldQuaternion(new THREE.Quaternion()));
    if (!l.positionX) {
      // Firefox: only the deprecated setters exist
      const ll = l as unknown as { setPosition(x: number, y: number, z: number): void; setOrientation(...v: number[]): void };
      ll.setPosition(this.pos.x, this.pos.y, this.pos.z);
      ll.setOrientation(this.fwd.x, this.fwd.y, this.fwd.z, this.up.x, this.up.y, this.up.z);
      return;
    }
    const t = this.ctx.currentTime;
    l.positionX.setValueAtTime(this.pos.x, t); l.positionY.setValueAtTime(this.pos.y, t); l.positionZ.setValueAtTime(this.pos.z, t);
    l.forwardX.setValueAtTime(this.fwd.x, t); l.forwardY.setValueAtTime(this.fwd.y, t); l.forwardZ.setValueAtTime(this.fwd.z, t);
    l.upX.setValueAtTime(this.up.x, t); l.upY.setValueAtTime(this.up.y, t); l.upZ.setValueAtTime(this.up.z, t);
  }

  get voiceCount(): number { return this.voices.count(); }
}
